import { AppUtils } from '../../utils/index';
/**
 * Model for transferring application data to the membership form application (1-stufigen).
 */
export class ApplicationMglInformationModel {
	private applicationName: string;
	private _custom: { [key: string]: any };
	private _input: ApplicationMglInformationInputModel;
	private _config: ApplicationMglInformationConfigModel;

	constructor() {
		this._config = new ApplicationMglInformationConfigModel();
		this._input = new ApplicationMglInformationInputModel();
		this._custom = {};
	}

	/**
	 * Configuration values are passed to and processed by the membership application form.
	 *
	 */
	get config(): ApplicationMglInformationConfigModel {
		return this._config;
	}

	/**
	 * Configuration values are passed to and processed by the membership application form.
	 *
	 */
	set config(value: ApplicationMglInformationConfigModel) {
		this._config = value;
	}

	/**
	 * Input values are directly rendered as hidden input fields into the membership application form.
	 *
	 */
	get input(): ApplicationMglInformationInputModel {
		return this._input;
	}

	/**
	 * Input values are directly rendered as hidden input fields into the membership application form.
	 *
	 */
	set input(value: ApplicationMglInformationInputModel) {
		this._input = value;
	}

	/**
	 * Custom dictionary object with key value pairs to additionally render as hidden input fields into the membership application form.
	 *
	 */
	get custom(): { [key: string]: any } {
		return this._custom;
	}

	/**
	 * Custom dictionary object with key value pairs to additionally render as hidden input fields into the membership application form.
	 *
	 */
	set custom(value: { [key: string]: any }) {
		this._custom = value;
	}

	get ApplicationName(): string {
		return this.applicationName;
	}

	set ApplicationName(value: string) {
		this.applicationName = value;
	}

	/**
	 * @return true, if the application mgl information holds valid data.
	 * 					 Returns false, if the application mgl information is empty.
	 */
	get Valid(): boolean {
		return !!this.applicationName && !!this._config && !!this._input;
	}

	public toString(): string {
		return JSON.stringify(this);
	}

	/**
	 * Creates a new ApplicationMglInformationModel object and copies the values from the provided data object (or other ApplicationMglInformation model) to the created ApplicationMglInformationModel.
	 */
	static createFromObject(dataObject: { [key: string]: any }): ApplicationMglInformationModel {
		return AppUtils.Helper.createFromMap(dataObject, ApplicationMglInformationModel, (key: string, value: any) => {
			switch (key) {
				case '_config':
				case 'config':
					return ApplicationMglInformationConfigModel.createFromObject(value);
				case '_input':
				case 'input':
					return ApplicationMglInformationInputModel.createFromObject(value);
				default:
					return value;
			}
		});
	}
}

/**
 * Configuration values are passed to and processed by the membership application form.
 * (also see utils.appconnection.js)
 *
 * @export
 * @class ApplicationMglInformationConfigModel
 */
export class ApplicationMglInformationConfigModel {
	private _processUrl: string;
	private _membershipadvertisingkey: string;
	private _membership: number;
	private geburtsdatum: string;

	get Geburtsdatum(): string {
		return this.geburtsdatum;
	}

	set Geburtsdatum(value: string) {
		this.geburtsdatum = value;
	}

	/**
	 * The membership (BASIS / PLUS) to show the membership application form for.
	 *
	 * @return 1 for BASIS membership, 2 for PLUS membership.
	 */
	get membership(): number {
		return this._membership;
	}

	/**
	 * The membership (BASIS / PLUS) to show the membership application form for.
	 *
	 * @param value 1 for BASIS membership, 2 for PLUS membership.
	 */
	set membership(value: number) {
		this._membership = value;
	}

	/**
	 * The product specific membership advertising key, that shall overwrite the default advertising key of the membership application.
	 */
	get membershipadvertisingkey(): string {
		return this._membershipadvertisingkey;
	}

	/**
	 * The product specific membership advertising key, that shall overwrite the default advertising key of the membership application.
	 */
	set membershipadvertisingkey(value: string) {
		this._membershipadvertisingkey = value;
	}

	/**
	 * Redirect url after registration.
	 * The user can optionally perform a registration after he completed the membership application form. If the user performs the registration step, the user is redirected
	 * to this url after completion.
	 *
	 * @return registration redirect url.
	 */
	get processUrl(): string {
		return this._processUrl;
	}

	/**
	 * Redirect url after registration.
	 * The user can optionally perform a registration after he completed the membership application form. If the user performs the registration step, the user is redirected
	 * to this url after completion.
	 *
	 * @param value registration redirect url.
	 */
	set processUrl(value: string) {
		this._processUrl = value;
	}

	public toString(): string {
		return JSON.stringify(this);
	}

	/**
	 * Creates a new ApplicationMglInformationConfigModel object and copies the values from the provided data object (or other ApplicationMglInformationConfig model) to the created ApplicationMglInformationConfigModel.
	 */
	public static createFromObject(dataObject: { [key: string]: any }): ApplicationMglInformationConfigModel {
		return AppUtils.Helper.createFromMap(dataObject, ApplicationMglInformationConfigModel);
	}
}

/**
 * Input values are directly rendered as hidden input fields into the membership application form.
 * The idea behind this data: if the membership application fails (Nachbearbeitung), the customer can be contacted
 * and offered the insurance product he originally tried to buy.
 * (also see utils.appconnection.js)
 *
 * @export
 * @class ApplicationMglInformationInputModel
 */
export class ApplicationMglInformationInputModel {
	private formularId: string;
	private produktWerbeschluessel: string;
	private productKey: string;
	private versicherungsBeginn: string;
	private sofortSchutz: boolean;

	get VersicherungsBeginn(): string {
		return this.versicherungsBeginn;
	}

	set VersicherungsBeginn(value: string) {
		this.versicherungsBeginn = value;
	}

	get SofortSchutz(): boolean {
		return this.sofortSchutz;
	}

	set SofortSchutz(value: boolean) {
		this.sofortSchutz = value;
	}

	get ProductKey(): string {
		return this.productKey;
	}

	set ProductKey(value: string) {
		this.productKey = value;
	}

	get ProduktWerbeschluessel(): string {
		return this.produktWerbeschluessel;
	}

	set ProduktWerbeschluessel(value: string) {
		this.produktWerbeschluessel = value;
	}

	/**
	 * Developer defined id string to identify the calling insurance application form.
	 */
	get FormularId(): string {
		return this.formularId;
	}

	/**
	 * Developer defined id string to identify the calling insurance application form.
	 */
	set FormularId(value: string) {
		this.formularId = value;
	}

	public toString(): string {
		return JSON.stringify(this);
	}

	/**
	 * Creates a new ApplicationMglInformationInputModel object and copies the values from the provided data object (or other ApplicationMglInformationInput model) to the created ApplicationMglInformationInputModel.
	 */
	public static createFromObject(dataObject: { [key: string]: any }): ApplicationMglInformationInputModel {
		return AppUtils.Helper.createFromMap(dataObject, ApplicationMglInformationInputModel);
	}
}
